import { AppIconProps } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { CareerPath, CareerPathResponse } from './careerPath';
import { CareerTrack } from './careerTrack';
import { Coach } from './coach';
import { College } from './college';
import { DeepDive } from './deepDive';
import { EducationalInvestment } from './educationalInvestment';
import { Employer } from './employer';
import { ExternalProfile } from './externalProfile';
import { GeneratedContent } from './generatedContent';
import { Industry } from './industry';
import { Internship } from './internship';
import { Major } from './major';
import { BackrsPost } from './post';
import { Scholarship } from './scholarship';
import { Tool } from './tool';
import {
  TrackAssessmentCategory,
  TrackAssessmentResult,
  TrackAssessmentUnlock,
} from './trackAssessment';
import { BackrsUser } from './user';

/** Minimal playlist info type for ContentNugget. */
type PlaylistInfo = Readonly<{
  id: number;
  title: string;
  description: string | null;
  total_tasks: number;
  completed_tasks: number;
  marketing_description?: string;
  preview_media?: {
    media_url: string;
  };
}>;

export type ContentCategory = Readonly<{
  id: number;
  title: string;
  sequence: number;
}>;

export type ContentCompetency = Readonly<{
  id: number;
  content_category_id: number;
  content_category_name: string;
  title: string;
  sequence: number;
  description: string;
  slug: string;
  unlock_id: number;
  unlock: TrackAssessmentCategory;
  media_url?: string;
}>;

/**
 * NUGGET TYPES
 */

export const NUGGET_THEMES = {
  REAL_PEOPLE_REAL_STORIES: 'Real People, Real Stories',
  LEARN_AND_EXPLORE: 'Learn & Explore',
  GROW_BY_DOING: 'Grow by Doing',
} as const;

export type NuggetTheme = (typeof NUGGET_THEMES)[keyof typeof NUGGET_THEMES];

/* The string should match the variants in the database  */
export const NUGGET_VARIANTS = {
  ARTICLE: 'article',
  VIDEO_EXTERNAL: 'video-external',
  VIDEO_INTERNAL: 'video-internal',
  TEXT_RESPONSE: 'text-response',
  POST: 'post',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  PROFILE_EXTERNAL: 'profile-external',
  PROFILE_INTERNAL: 'profile-internal',
  INSTITUTION: 'institution',
  OPPORTUNITY: 'opportunity',
  EVENT: 'event',
  BOOK: 'book',
  COMPETENCY: 'competency',
  SCHOLARSHIPS: 'scholarships',
  CAREER_PATH: 'career-path',
  CAREER_TRACK: 'career-track',
  INTERNSHIP: 'internship',
  RESOURCE: 'resource',
  GENERATED_CONTENT: 'generated-content',
  TRACK_ASSESSMENT_RESULT: 'track-assessment-result',
  EMPLOYER: 'employer',
  TRACK_ASSESSMENT_UNLOCK: 'track-assessment-unlock',
  EDUCATIONAL_INVESTMENT: 'educational-investment',
  TOOL: 'tool',
  MAJOR: 'major',
  INDUSTRY: 'industry',
  COACH: 'coach',
  DEEP_DIVE: 'deep-dive',
  PLAYLIST: 'playlist',
  SOLUTION_SESSION: 'solution-session',
  BLUEPRINT: 'blueprint',
} as const;

export type NuggetVariant = (typeof NUGGET_VARIANTS)[keyof typeof NUGGET_VARIANTS];

export const NUGGET_TYPES = {
  BACKRS_PROFILE_PROFESSIONAL: 'backrs-profile-professional',
  BACKRS_PROFILE_STUDENT: 'backrs-profile-student',
  BACKRS_PROFILE_SERVICE_PROVIDER: 'backrs-profile-service-provider',
  EXTERNAL_PROFILE_PROFESSIONAL: 'external-profile-professional',
  EXTERNAL_PROFILE_STUDENT: 'external-profile-student',
  EXTERNAL_PROFILE_SERVICE_PROVIDER: 'external-profile-service-provider',
  INSTITUTION_HIGH_SCHOOL: 'institution-high-school',
  INSTITUTION_UNIVERSITY: 'institution-university',
  INSTITUTION_EMPLOYER: 'institution-employer',
  INSTITUTION_NONPROFIT: 'institution-nonprofit',
  INSTITUTION_OTHER: 'institution-other',
  NETWORK: 'network',
  CONTENT: 'content',
  ACTIVITY: 'activity',
  OPPORTUNITY: 'opportunity',
  EMPLOYER: 'employer',
  TRACK_ASSESSMENT_RESULT: 'track-assessment-result',
  TRACK_ASSESSMENT_UNLOCK: 'track-assessment-unlock',
  TOOL: 'tool',
} as const;

export type NuggetType = (typeof NUGGET_TYPES)[keyof typeof NUGGET_TYPES];

export const NUGGET_FORMATS = {
  VIDEO: 'video',
  EVENT: 'event',
  AUDIO_FILE: 'audio-file',
  RESUMES_CV: 'resumes-cv',
  TRANSCRIPT: 'transcript',
  DOCUMENT: 'document',
  BOOK: 'book',
  PRODUCT: 'product',
  SERVICE: 'service',
  APP: 'app',
  COURSE: 'course',
  PROFILE: 'profile',
  POST: 'post',
  ARTICLE: 'article',
  PODCAST: 'podcast',
  URL_DESCRIPTION: 'url-description',
  SHADOWING: 'shadowing',
  INTERNSHIP: 'internship',
  FULL_TIME_POSITION: 'full-time-position',
  RESEARCH: 'research',
  VOLUNTEERING: 'volunteering',
  ENRICHMENT: 'enrichment',
  SCHOLARSHIPS: 'scholarships',
} as const;

export type NuggetFormat = (typeof NUGGET_FORMATS)[keyof typeof NUGGET_FORMATS];

export type ContentNugget = Readonly<{
  id: number;
  slug: string;
  title: string;
  description: string;
  external_url?: string;
  star_rating: number;
  value_rating: number;
  media_id?: number;
  media?: {
    id: number;
    media_url: string;
  };
  created_by_user_id?: number;
  createdByUser?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  sponsor_id?: number;
  start_date?: string;
  end_date?: string;
  variant: NuggetVariant;
  content_nugget_theme?: NuggetTheme | string;
  format?: NuggetFormat;
  type?: NuggetType;
  tags?: string;
  preview_media_id?: number;
  preview_media?: {
    id: number;
    media_url: string;
  };
  is_browsable: boolean;

  internship_info?: Internship;
  scholarship_info?: Scholarship;
  profile_info?: BackrsUser;
  career_path_info?: CareerPath;
  career_track_info?: CareerTrack;
  post_info?: BackrsPost;
  external_profile_info?: ExternalProfile;
  college_info?: College;
  generated_content_info?: GeneratedContent;
  employer_info?: Employer;
  track_assessment_result_info?: TrackAssessmentResult;
  track_assessment_unlock_info?: TrackAssessmentUnlock;
  educational_investment_info?: EducationalInvestment;
  tool_info?: Tool;
  major_info?: Major;
  industry_info?: Industry;
  coach_info?: Coach;
  deep_dive_info?: DeepDive;
  playlist_info?: PlaylistInfo;
  blueprint_info?: { career_user_response_set_id: string };
}>;

export const NUGGET_VALIDATION_TYPES = {
  BACKRS: 'Backrs',
  USER: 'User',
  EXPERT: 'Expert',
  SPONSOR: 'Sponsor',
};

export type NuggetValidationType =
  (typeof NUGGET_VALIDATION_TYPES)[keyof typeof NUGGET_VALIDATION_TYPES];

export type ContentNuggetBookmarkTypes =
  | 'nugget'
  | 'scholarship'
  | 'career-path'
  | 'career-track'
  | 'profile'
  | 'external-profile'
  | 'internship'
  | 'post'
  | 'college'
  | 'generated-content'
  | 'track-assessment-result'
  | 'track-assessment-unlock'
  | 'educational-investment'
  | 'tool'
  | 'employer'
  | 'competency'
  | 'major'
  | 'industry'
  | 'coach'
  | 'deep-dive'
  | 'blueprint';

export type ContentNuggetBookmark = Readonly<{
  id: number;
  user_id: number;
  content_nugget?: ContentNugget;
  content_scholarship?: Scholarship;
  content_profile?: BackrsUser;
  content_internship?: Internship;
  content_career_path?: CareerPathResponse;
  content_career_track?: CareerTrack;
  content_external_profile?: ExternalProfile;
  content_college?: College;
  content_post?: BackrsPost;
  content_generated?: GeneratedContent;
  content_track_assessment_result?: TrackAssessmentResult;
  content_track_assessment_unlock?: TrackAssessmentUnlock;
  content_educational_investment?: EducationalInvestment;
  content_tool?: Tool;
  content_employer?: Employer;
  content_competency?: ContentCompetency;
  content_major?: Major;
  content_industry?: Industry;
  content_coach?: Coach;
  content_deep_dive?: DeepDive;
  content_blueprint?: { id: string };
  created_at: string;
  updated_at: string;
}>;

export type PanelNuggetActionParams = Readonly<{
  /** will default to bookmark add icon */
  icon?: AppIconProps;
  hasBookmarked?: boolean;
  onClick: () => void;
  handleActionBookmark?: () => void;
  handleActionAddTask?: () => void;
  handleActionShare?: () => void;
}>;
